import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader, Text, Button } from '@vibe/core';
import { useTimesheetStore } from '../../stores/timesheetStore';
import { useUsersStore } from '../../stores/usersStore';
import { useTimesheetData } from '../../hooks/useTimesheetData';
import { useProgressData } from '../../hooks/useProgressData';
import { useMonday } from '../../hooks/useMonday';
import { useCurrentUserAccess } from '../../hooks/useCurrentUserAccess';
import { getGroupingConfig } from '../../config/timesheet-grouping';
import { fetchMondayUsers } from '../../api/monday.api';
import { getMyViewableTargets } from '../../api/permission.api';
import { resolveAllowedUserIds, hasApprovePermission } from '../../utils/timesheet-permissions';
import TimesheetSidebar from './TimesheetSidebar';
import TimesheetTopBar from './TimesheetTopBar';
import TimesheetTimeline from './TimesheetTimeline';
import TimesheetFilterBar from './TimesheetFilterBar';
import ProgressTable from './ProgressTable';
import WorklogDetailDialog from './WorklogDetailDialog';
import TimesheetApproverView from '../timesheet-approval/TimesheetApproverView';
import TimesheetReportView from './TimesheetReportView';
import type {
  TimesheetRow,
  TimesheetGroupBy,
  ProgressViewType,
  ProgressGroup,
} from '../../types/timesheet.types';
import type { PickedBoard } from '../shared/BoardPicker';
import {
  createWorkbook, addTitleRow, styleHeaderRow, styleGroupRow, styleDataRow, styleTotalRow,
  setColumnWidths, downloadWorkbook, fillCell, getProgressColor, round, hexToArgb, COLORS,
} from '../../utils/export-report-excel';
import '../../styles/timesheet.css';

// ─── Excel Export (Timeline) ────────────────────────────────────────────────

async function exportToExcel(
  rows: TimesheetRow[],
  dateColumns: string[],
  startDate: string,
  endDate: string,
  groupByLabel: string,
  showProgress: boolean,
): Promise<void> {
  try {
  const wb = createWorkbook();
  const ws = wb.addWorksheet('Timesheet');

  addTitleRow(ws, `Timesheet (${groupByLabel})`, startDate, endDate);

  // Header — matches the web: Entity, optional Progress %, Sum (h), then one column per date
  const headers: string[] = ['Entity'];
  if (showProgress) headers.push('Progress %');
  headers.push('Sum (h)', ...dateColumns);

  const headerRow = ws.addRow(headers);
  styleHeaderRow(ws, headerRow);

  const dateStartCol = showProgress ? 4 : 3;

  setColumnWidths(ws, [
    30,
    ...(showProgress ? [12] : []),
    12,
    ...dateColumns.map(() => 12),
  ]);

  const addTimesheetRow = (row: TimesheetRow, indent: boolean) => {
    const rowData: (string | number)[] = [row.label];
    if (showProgress) rowData.push(row.progress !== null ? round(row.progress) : '');
    rowData.push(round(row.totalActual));
    for (const d of dateColumns) {
      const val = row.cells[d]?.actual ?? 0;
      rowData.push(val > 0 ? round(val) : '');
    }

    const excelRow = ws.addRow(rowData);
    styleDataRow(excelRow, indent);

    // Progress % formatting and color
    if (showProgress) {
      const pctCell = excelRow.getCell(2);
      if (row.progress !== null) {
        pctCell.numFmt = '0.00';
        const color = getProgressColor(row.progress);
        fillCell(pctCell, color.bg, color.font, true);
      }
    }

    // Center-align date + sum cells
    for (let i = dateStartCol - 1; i <= headers.length; i++) {
      excelRow.getCell(i).alignment = { horizontal: 'center', vertical: 'middle' };
    }

    if (!indent) {
      excelRow.getCell(1).font = { bold: true, size: 10 };
    } else {
      excelRow.getCell(1).font = { size: 9, color: { argb: hexToArgb(COLORS.secondaryText) } };
    }
  };

  const writeRowRecursive = (row: TimesheetRow) => {
    addTimesheetRow(row, row.depth > 0);
    if (row.children) {
      for (const child of row.children) {
        writeRowRecursive(child);
      }
    }
  };

  for (const row of rows) {
    writeRowRecursive(row);
  }

  await downloadWorkbook(wb, `timesheet_${groupByLabel}_${startDate}_${endDate}.xlsx`);
  } catch (err) {
    console.error('Excel export failed:', err);
  }
}

// ─── Excel Export (Progress) ────────────────────────────────────────────────

const PROGRESS_HEADERS: Record<ProgressViewType, string[]> = {
  progress_timesheet: ['User', 'Required Hours', 'Logged Hours', 'Billable Hours', 'Non-Billable Hours', 'Progress %'],
  progress_utilization: ['User', 'Required Hours', 'Planned Hours', 'Utilization %'],
  progress_planned_vs_actual: ['User', 'Planned Hours', 'Logged Hours', 'Billable Hours', 'Non-Billable Hours', 'Progress %'],
};

const PROGRESS_LABELS: Record<ProgressViewType, string> = {
  progress_timesheet: 'Progress Timesheet',
  progress_utilization: 'Progress Utilization',
  progress_planned_vs_actual: 'Progress Planned vs Actual',
};

function getProgressValues(
  row: { requiredHours: number; loggedHours: number; plannedHours: number; billableHours: number | null },
  progressType: ProgressViewType,
): { col1: number; col2: number; billable: number | null; nonBillable: number | null; percent: number } {
  const billable = row.billableHours;
  const nonBillable = billable != null ? row.loggedHours - billable : null;
  switch (progressType) {
    case 'progress_timesheet':
      return {
        col1: row.requiredHours,
        col2: row.loggedHours,
        billable,
        nonBillable,
        percent: row.requiredHours > 0 ? (row.loggedHours / row.requiredHours) * 100 : 0,
      };
    case 'progress_utilization':
      return {
        col1: row.requiredHours,
        col2: row.plannedHours,
        billable: null,
        nonBillable: null,
        percent: row.requiredHours > 0 ? (row.plannedHours / row.requiredHours) * 100 : 0,
      };
    case 'progress_planned_vs_actual':
      return {
        col1: row.plannedHours,
        col2: row.loggedHours,
        billable,
        nonBillable,
        percent: row.plannedHours > 0 ? (row.loggedHours / row.plannedHours) * 100 : 0,
      };
  }
}

async function exportProgressToExcel(
  groups: ProgressGroup[],
  progressType: ProgressViewType,
  startDate: string,
  endDate: string,
): Promise<void> {
  try {
  const wb = createWorkbook();
  const ws = wb.addWorksheet(PROGRESS_LABELS[progressType]);

  addTitleRow(ws, PROGRESS_LABELS[progressType], startDate, endDate);

  const headers = PROGRESS_HEADERS[progressType];
  const headerRow = ws.addRow(headers);
  styleHeaderRow(ws, headerRow);

  const hasBillable = progressType !== 'progress_utilization';
  const pctColIdx = headers.length; // last column is %

  setColumnWidths(ws, [
    24,
    ...headers.slice(1).map(() => 16),
  ]);

  for (const group of groups) {
    if (groups.length > 1) {
      const groupRow = ws.addRow([
        `${group.groupName} (${group.rows.length} users)`,
        ...new Array(headers.length - 1).fill(''),
      ]);
      styleGroupRow(groupRow);
    }

    for (const row of group.rows) {
      const vals = getProgressValues(row, progressType);
      const rowData: (string | number)[] = [row.userName, round(vals.col1), round(vals.col2)];
      if (hasBillable) {
        rowData.push(vals.billable != null ? round(vals.billable) : '');
        rowData.push(vals.nonBillable != null ? round(vals.nonBillable) : '');
      }
      rowData.push(round(vals.percent));

      const excelRow = ws.addRow(rowData);
      styleDataRow(excelRow);
      excelRow.getCell(1).font = { bold: true, size: 10 };

      // % formatting and color
      const pctCell = excelRow.getCell(pctColIdx);
      pctCell.numFmt = '0.00';
      pctCell.alignment = { horizontal: 'center', vertical: 'middle' };
      const color = getProgressColor(vals.percent);
      fillCell(pctCell, color.bg, color.font, true);

      // Center number cells
      for (let i = 2; i < pctColIdx; i++) {
        excelRow.getCell(i).alignment = { horizontal: 'center', vertical: 'middle' };
        excelRow.getCell(i).numFmt = '0.00';
      }
    }
  }

  // Grand totals
  const allRows = groups.flatMap((g) => g.rows);
  const totalBillable = allRows.every((r) => r.billableHours == null)
    ? null
    : allRows.reduce((s, r) => s + (r.billableHours ?? 0), 0);
  const totals = {
    requiredHours: allRows.reduce((s, r) => s + r.requiredHours, 0),
    loggedHours: allRows.reduce((s, r) => s + r.loggedHours, 0),
    plannedHours: allRows.reduce((s, r) => s + r.plannedHours, 0),
    billableHours: totalBillable,
  };
  const totalVals = getProgressValues(totals, progressType);
  const totalRowData: (string | number)[] = [
    'Total',
    round(totalVals.col1),
    round(totalVals.col2),
  ];
  if (hasBillable) {
    totalRowData.push(totalVals.billable != null ? round(totalVals.billable) : '');
    totalRowData.push(totalVals.nonBillable != null ? round(totalVals.nonBillable) : '');
  }
  totalRowData.push(round(totalVals.percent));

  const totalRow = ws.addRow(totalRowData);
  styleTotalRow(totalRow);
  totalRow.getCell(pctColIdx).numFmt = '0.00';
  totalRow.getCell(pctColIdx).alignment = { horizontal: 'center', vertical: 'middle' };
  for (let i = 2; i < pctColIdx; i++) {
    totalRow.getCell(i).alignment = { horizontal: 'center', vertical: 'middle' };
    totalRow.getCell(i).numFmt = '0.00';
  }

  await downloadWorkbook(wb, `${progressType.replace(/_/g, '-')}_${startDate}_${endDate}.xlsx`);
  } catch (err) {
    console.error('Excel export failed:', err);
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

const TimesheetWorkspace: React.FC = () => {
  // Connect to store
  const {
    groupBy,
    period,
    dateRange,
    filters,
    rows,
    capacityMap,
    dateColumns,
    expandedRowKeys,
    sidebarCollapsed,
    isLoading,
    error,
    progressGroups,
    progressLoading,
    progressError,
    setGroupBy,
    setPeriod,
    setCustomDateRange,
    setFilters,
    clearFilters,
    navigateDatePrev,
    navigateDateNext,
    navigateToToday,
    toggleRow,
    expandAll,
    collapseAll,
    setSidebarCollapsed,
    setError,
    setProgressError,
    subGroupBy,
    setSubGroupBy,
  } = useTimesheetStore();

  // Users from shared store
  const users = useUsersStore((s) => s.users);
  const setUsers = useUsersStore((s) => s.setUsers);

  // monday.com context for current user
  const { context } = useMonday();
  const currentUserId = context?.userId ? String(context.userId) : '';
  const { isAdmin, isLoading: isAccessLoading } = useCurrentUserAccess();

  // ─── Permission-aware user filtering ────────────────────────────────────────
  // undefined = not resolved yet (still loading)
  // null = admin, no filter (sees everyone)
  // number[] = resolved set of allowed user IDs
  const [allowedUserIds, setAllowedUserIds] = useState<number[] | null | undefined>(undefined);

  useEffect(() => {
    if (isAccessLoading || users.length === 0) return;
    if (isAdmin) {
      setAllowedUserIds(null);
      return;
    }
    const currentId = context?.userId ? Number(context.userId) : null;
    getMyViewableTargets()
      .then((resp) => {
        setIsApprover(hasApprovePermission(resp));
        setAllowedUserIds(resolveAllowedUserIds(resp, currentId, users));
      })
      .catch(() => {
        setAllowedUserIds(currentId ? [currentId] : []);
      });
  }, [isAccessLoading, isAdmin, context?.userId, users]);

  const [isApprover, setIsApprover] = useState(false);
  const permissionsResolved = allowedUserIds !== undefined;
  const showApprovals = isAdmin || isApprover;


  // Determine if current view is an approval view
  const APPROVAL_VIEWS: TimesheetGroupBy[] = ['approval_pending', 'approval_history'];
  const isApprovalView = APPROVAL_VIEWS.includes(groupBy);

  // Redirect away from approval views if user doesn't have approval permissions
  useEffect(() => {
    if (permissionsResolved && isApprovalView && !showApprovals) {
      setGroupBy('summary');
    }
  }, [permissionsResolved, isApprovalView, showApprovals, setGroupBy]);

  // Clear all filters when entering the timesheet page (fresh start)
  useEffect(() => {
    clearFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure users are loaded
  useEffect(() => {
    if (users.length === 0) {
      fetchMondayUsers()
        .then((fetched) => setUsers(fetched))
        .catch((err) => console.error('Failed to fetch users:', err));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build user & team options for the filter bar (filtered by permissions)
  const userOptions = useMemo(() => {
    if (!permissionsResolved) return []; // still loading — show nothing
    const list = Array.isArray(allowedUserIds)
      ? users.filter((u) => allowedUserIds.includes(u.id))
      : users; // null = admin, no filter
    return list.map((u) => ({
      id: u.id,
      name: u.name,
      avatarUrl: u.photo_thumb_small ?? undefined,
    }));
  }, [users, allowedUserIds, permissionsResolved]);

  const teamMap = useMemo(() => {
    const map = new Map<
      number,
      { id: number; name: string; memberIds: Set<number> }
    >();
    for (const user of users) {
      for (const team of user.teams) {
        let entry = map.get(team.id);
        if (!entry) {
          entry = { id: team.id, name: team.name, memberIds: new Set() };
          map.set(team.id, entry);
        }
        entry.memberIds.add(user.id);
      }
    }
    return map;
  }, [users]);

  const teamOptions = useMemo(() => {
    if (!permissionsResolved) return []; // still loading
    const allowedSet = Array.isArray(allowedUserIds) ? new Set(allowedUserIds) : null;
    return Array.from(teamMap.values())
      .filter((entry) => {
        if (!allowedSet) return true; // admin sees all teams
        return Array.from(entry.memberIds).some((id) => allowedSet.has(id));
      })
      .map((entry) => ({
        id: entry.id,
        name: entry.name,
        memberNames: Array.from(entry.memberIds)
          .map((id) => users.find((u) => u.id === id)?.name)
          .filter(Boolean) as string[],
      }));
  }, [teamMap, users, allowedUserIds, permissionsResolved]);

  // Handle user selection from filter bar
  const handleUserSelectionChange = useCallback(
    (userIds: number[]) => {
      setFilters({ userIds: userIds.map(String) });
    },
    [setFilters],
  );

  // Handle team selection — resolve team members to user IDs (filtered by permissions)
  const handleTeamSelectionChange = useCallback(
    (teamIds: number[]) => {
      const allowedSet = Array.isArray(allowedUserIds) ? new Set(allowedUserIds) : null;
      const userIdSet = new Set<number>();
      for (const teamId of teamIds) {
        const entry = teamMap.get(teamId);
        if (entry) {
          for (const memberId of entry.memberIds) {
            if (!allowedSet || allowedSet.has(memberId)) {
              userIdSet.add(memberId);
            }
          }
        }
      }
      setFilters({
        userIds: Array.from(userIdSet).map(String),
        teamIds,
      });
    },
    [teamMap, setFilters, allowedUserIds],
  );

  // Track selected boards with names (for chip display)
  // Starts empty — filters are cleared on mount
  const [selectedBoards, setSelectedBoards] = useState<PickedBoard[]>([]);

  // Handle board selection from filter bar
  const handleBoardSelectionChange = useCallback(
    (boards: PickedBoard[]) => {
      setSelectedBoards(boards);
      setFilters({ boardIds: boards.map((b) => b.id) });
    },
    [setFilters],
  );

  // Trigger data fetching (each hook self-guards based on viewMode)
  useTimesheetData();
  useProgressData();

  // Derive view mode and config
  const groupingConfig = useMemo(
    () => getGroupingConfig(groupBy),
    [groupBy],
  );

  const viewMode = groupingConfig.viewMode;

  const hasExpandableRows = useMemo(
    () => rows.some((r) => r.expandable),
    [rows],
  );

  // Report view export ref
  const reportExportRef = React.useRef<(() => void) | null>(null);
  const handleReportExportReady = useCallback((fn: (() => void) | null) => {
    reportExportRef.current = fn;
  }, []);

  // Export handler
  const handleExport = useCallback(() => {
    if (viewMode === 'report') {
      reportExportRef.current?.();
    } else if (viewMode === 'progress') {
      exportProgressToExcel(
        progressGroups,
        groupBy as ProgressViewType,
        dateRange.start,
        dateRange.end,
      );
    } else {
      exportToExcel(
        rows,
        dateColumns,
        dateRange.start,
        dateRange.end,
        groupingConfig.label.replace(/\s+/g, '-').toLowerCase(),
        groupingConfig.category !== 'board_timesheets',
      );
    }
  }, [viewMode, progressGroups, groupBy, rows, dateColumns, dateRange, groupingConfig]);

  // Worklog detail dialog state
  const [dialogState, setDialogState] = useState<{
    row: TimesheetRow;
    parentRow?: TimesheetRow;
    date: string;
  } | null>(null);

  // Cell click handler
  const handleCellClick = useCallback(
    (row: TimesheetRow, date: string, parentRow?: TimesheetRow) => {
      setDialogState({ row, parentRow, date });
    },
    [],
  );

  // Selection state
  const hasSelection = filters.userIds.length > 0 || (filters.teamIds?.length ?? 0) > 0 || filters.boardIds.length > 0;

  return (
    <div className="rp-ts-workspace">
      {/* Sidebar */}
      <TimesheetSidebar
        activeGroupBy={groupBy}
        onSelect={setGroupBy}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        showApprovals={showApprovals}
      />

      {/* Main area */}
      <div className="rp-ts-workspace__main">
        {isApprovalView ? (
          // ── Approval View ──────────────────────────────────────────
          <TimesheetApproverView
            currentUserId={currentUserId}
            isAdmin={isAdmin}
          />
        ) : (
          <>
            {/* Top bar */}
            <TimesheetTopBar
              groupBy={groupBy}
              period={period}
              dateRange={dateRange}
              viewMode={viewMode}
              viewLabel={groupingConfig.label}
              onPeriodChange={setPeriod}
              onCustomRangeChange={setCustomDateRange}
              onDatePrev={navigateDatePrev}
              onDateNext={navigateDateNext}
              onToday={navigateToToday}
              onExport={handleExport}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
              hasExpandableRows={hasExpandableRows}
              subGroupBy={subGroupBy}
              onSubGroupByChange={setSubGroupBy}
            />

            {/* Filter bar */}
            <TimesheetFilterBar
              userOptions={userOptions}
              teamOptions={teamOptions}
              selectedUserIds={
                (filters.teamIds?.length ?? 0) > 0
                  ? []
                  : filters.userIds.map(Number).filter((n) => !isNaN(n))
              }
              selectedTeamIds={filters.teamIds ?? []}
              selectedBoards={selectedBoards}
              onUserSelectionChange={handleUserSelectionChange}
              onTeamSelectionChange={handleTeamSelectionChange}
              onBoardSelectionChange={handleBoardSelectionChange}
            />

            {/* Content area */}
            <div className="rp-ts-workspace__content">
              {viewMode === 'report' ? (
                <TimesheetReportView onExportReady={handleReportExportReady} />
              ) : viewMode === 'timeline' ? (
                isLoading ? (
                  <div className="rp-ts-workspace__loading">
                    <Loader size={Loader.sizes.MEDIUM} />
                  </div>
                ) : error ? (
                  <div className="rp-ts-workspace__error">
                    <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
                      {error}
                    </Text>
                    <Button
                      size={Button.sizes.SMALL}
                      kind={Button.kinds.TERTIARY}
                      onClick={() => setError(null)}
                      style={{ marginTop: '8px' }}
                    >
                      Dismiss
                    </Button>
                  </div>
                ) : (
                  <TimesheetTimeline
                    rows={rows}
                    dateColumns={dateColumns}
                    capacityMap={capacityMap}
                    expandedRowKeys={expandedRowKeys}
                    onToggleRow={toggleRow}
                    onCellClick={handleCellClick}
                    isLoading={isLoading}
                    hasSelection={hasSelection}
                    showProgress={groupingConfig.category !== 'board_timesheets'}
                  />
                )
              ) : (
                progressLoading ? (
                  <div className="rp-ts-workspace__loading">
                    <Loader size={Loader.sizes.MEDIUM} />
                  </div>
                ) : progressError ? (
                  <div className="rp-ts-workspace__error">
                    <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
                      {progressError}
                    </Text>
                    <Button
                      size={Button.sizes.SMALL}
                      kind={Button.kinds.TERTIARY}
                      onClick={() => setProgressError(null)}
                      style={{ marginTop: '8px' }}
                    >
                      Dismiss
                    </Button>
                  </div>
                ) : (
                  <ProgressTable
                    groups={progressGroups}
                    progressType={groupBy as ProgressViewType}
                    hasSelection={hasSelection}
                  />
                )
              )}
            </div>
          </>
        )}
      </div>

      {/* Worklog detail dialog */}
      {dialogState && (
        <WorklogDetailDialog
          row={dialogState.row}
          parentRow={dialogState.parentRow}
          date={dialogState.date}
          groupBy={groupBy}
          onClose={() => setDialogState(null)}
        />
      )}
    </div>
  );
};

export default TimesheetWorkspace;
